import {ICacheable} from '../../@interfaces/classes/Cacheable/ICacheable';

export default abstract class Cacheable<TCache = any> implements ICacheable {
    #cacheEnabled = true;

    protected cache: Record<string, TCache> = {};

    flushCache(): void {
        this.cache = {};
    }

    withoutCache<T = any>(callable: (instance: ICacheable, ...args: any[]) => T, ...args: any[]): T {
        const previousState = this.#cacheEnabled;
        this.#cacheEnabled = false;
        const result = callable(this, ...args);
        this.#cacheEnabled = previousState;

        return result;
    }

    enableCache() {
        this.#cacheEnabled = true;
    }

    disableCache() {
        this.#cacheEnabled = false;
    }

    protected hasCache(key: string): boolean {
        return this.#cacheEnabled && (key in this.cache);
    }

    protected getCache(key: string): TCache | null {
        return this.#cacheEnabled ? this.cache[key] ?? null : null;
    }

    protected setCache(key: string, value: TCache): ICacheable {
        this.cache[key] = value;

        return this;
    }

    protected forget(key: string): void {
        if (this.cache.hasOwnProperty(key)) {
            delete this.cache[key];
        }
    }

    protected remember<TResult>(
        key: string,
        resultProvider: () => TResult,
        shouldInvalidate?: (cachedResult: TResult) => boolean
    ): TResult {
        const cacheKey = this.getCacheKeyForRemember(key);
        const cache = this.getCache(cacheKey);
        const shouldReCache = shouldInvalidate ?? ((cache: any) => cache === null);
        if (!shouldReCache(cache as TResult)) {
            return cache as TResult;
        }

        const result = resultProvider();
        this.setCache(cacheKey, result as any);

        return result;
    }

    protected forgetRemember(key: string): void {
        return this.forget(this.getCacheKeyForRemember(key));
    }

    protected stringify(item: any): string {
        return JSON.stringify(item);
    }

    protected getCacheKeyForRemember(key: string): string {
        return `remember@${key}`;
    }
}
